import React, { useState, useEffect } from 'react';
import { FaChartBar, FaChartLine, FaChartPie, FaDownload, FaCalendarAlt, FaFilter } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';
import { useSubscription } from '../../hooks/useSubscription';

/**
 * School analytics component
 * Displays performance metrics and data visualization
 */
const SchoolAnalytics = () => {
  const { showSuccess, showError } = useToast();
  const { hasTier } = useSubscription();

  // Analytics data state
  const [analyticsData, setAnalyticsData] = useState({
    performanceMetrics: {
      subjects: [],
      averageScores: []
    },
    enrollmentTrend: {
      years: [],
      counts: []
    },
    genderDistribution: {
      male: 0,
      female: 0
    },
    classPerformance: []
  });
  
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [filters, setFilters] = useState({
    academicYear: 'current',
    term: 'all',
    grade: 'all'
  });
  
  // Time period options
  const academicYears = ['2021-2022', '2022-2023', '2023-2024', 'current'];
  const terms = ['Term 1', 'Term 2', 'Term 3', 'all'];
  const grades = ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'all'];

  // Fetch analytics data on component mount and when filters change
  useEffect(() => {
    fetchAnalyticsData();
  }, [filters]);

  // Fetch analytics data from API
  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Prepare query params
      const params = new URLSearchParams();
      if (filters.academicYear !== 'all') params.append('academicYear', filters.academicYear);
      if (filters.term !== 'all') params.append('term', filters.term);
      if (filters.grade !== 'all') params.append('grade', filters.grade);
      
      const queryString = params.toString();
      const endpoint = `/schools/analytics${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(endpoint);
      setAnalyticsData(response.data.data);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      showError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Export analytics data as CSV
  const handleExportData = async () => {
    try {
      // Verify premium subscription
      if (!hasTier(['elevate'])) {
        showError('Export feature requires Elevate subscription');
        return;
      }
      
      // Request export
      const response = await api.get('/schools/analytics/export', {
        params: filters,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'school_analytics.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      showSuccess('Analytics data exported successfully');
    } catch (error) {
      console.error('Error exporting analytics data:', error);
      showError('Failed to export data');
    }
  };

  // Get color class based on performance score
  const getPerformanceColorClass = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-blue-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaChartBar className="mr-2 text-primary" /> School Analytics
          </h1>
          <p className="text-gray-600 mt-1">
            Analyze performance metrics and trends
          </p>
        </div>
        
        <div className="mt-4 md:mt-0">
          <button
            onClick={handleExportData}
            className="btn-secondary flex items-center"
            disabled={!hasTier(['elevate'])}
            title={!hasTier(['elevate']) ? 'Requires Elevate subscription' : ''}
          >
            <FaDownload className="mr-2" /> Export Data
          </button>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-soft p-6 mb-6">
        <div className="flex items-center mb-4">
          <FaFilter className="mr-2 text-primary" />
          <h2 className="text-lg font-medium">Filters</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Academic Year</label>
            <select
              name="academicYear"
              value={filters.academicYear}
              onChange={handleFilterChange}
              className="form-select w-full"
            >
              {academicYears.map(year => (
                <option key={year} value={year}>{year === 'current' ? 'Current Year' : year}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Term</label>
            <select
              name="term"
              value={filters.term}
              onChange={handleFilterChange}
              className="form-select w-full"
            >
              {terms.map(term => (
                <option key={term} value={term}>{term === 'all' ? 'All Terms' : term}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Grade</label>
            <select
              name="grade"
              value={filters.grade}
              onChange={handleFilterChange}
              className="form-select w-full"
            >
              {grades.map(grade => (
                <option key={grade} value={grade}>{grade === 'all' ? 'All Grades' : grade}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Analytics Content */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-8 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Performance Overview */}
          <div className="bg-white rounded-lg shadow-soft overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-lg flex items-center">
                <FaChartBar className="mr-2 text-primary" /> Academic Performance
              </h2>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <div className="text-sm text-gray-500 mb-2">
                  Average performance by subject
                </div>
                
                {analyticsData.performanceMetrics.subjects.map((subject, index) => {
                  const score = analyticsData.performanceMetrics.averageScores[index] || 0;
                  const colorClass = getPerformanceColorClass(score);
                  
                  return (
                    <div key={index} className="mb-3 last:mb-0">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">{subject}</span>
                        <span className="text-sm text-gray-500">{score}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className={`${colorClass} h-2.5 rounded-full`} 
                          style={{ width: `${score}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Class Performance Comparison */}
              <div className="mt-8">
                <div className="text-sm text-gray-500 mb-2">
                  Performance by class
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {analyticsData.classPerformance.map((classData, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-medium mb-2">{classData.name}</h3>
                      <div className="mb-2">
                        <div className="text-xs text-gray-500">Average Score</div>
                        <div className="text-2xl font-bold">{classData.averageScore}%</div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-3">
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Passing Rate</div>
                          <div className="font-medium">{classData.passingRate}%</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Top Score</div>
                          <div className="font-medium">{classData.topScore}%</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Students</div>
                          <div className="font-medium">{classData.studentCount}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Enrollment & Demographics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Enrollment Trends */}
            <div className="bg-white rounded-lg shadow-soft overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="font-semibold text-lg flex items-center">
                  <FaChartLine className="mr-2 text-primary" /> Enrollment Trends
                </h2>
              </div>
              
              <div className="p-6">
                <div className="text-sm text-gray-500 mb-4">
                  Student enrollment by year
                </div>
                
                {/* Simple Line Chart */}
                <div className="h-48 flex items-end relative">
                  <div className="absolute inset-0 flex flex-col justify-between">
                    {[0, 1, 2, 3, 4].map(index => (
                      <div key={index} className="border-b border-gray-200 relative h-0">
                        <span className="absolute -top-2 -left-2 text-xs text-gray-400">
                          {Math.round(Math.max(...analyticsData.enrollmentTrend.counts) / 4 * (4 - index))}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex-1 z-10 flex items-end pt-6">
                    {analyticsData.enrollmentTrend.years.map((year, index) => {
                      const count = analyticsData.enrollmentTrend.counts[index] || 0;
                      const maxCount = Math.max(...analyticsData.enrollmentTrend.counts);
                      const height = maxCount ? (count / maxCount) * 100 : 0;
                      
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center">
                          <div 
                            className="w-3 bg-primary rounded-full" 
                            style={{ height: `${height}%` }}
                          ></div>
                          <div className="text-xs mt-2 text-gray-500">{year}</div>
                          <div className="text-xs font-medium">{count}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Gender Distribution */}
            <div className="bg-white rounded-lg shadow-soft overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="font-semibold text-lg flex items-center">
                  <FaChartPie className="mr-2 text-primary" /> Gender Distribution
                </h2>
              </div>
              
              <div className="p-6">
                <div className="text-sm text-gray-500 mb-4">
                  Student population by gender
                </div>
                
                {/* Simple Pie Chart Representation */}
                <div className="flex justify-center mb-6">
                  <div className="w-40 h-40 rounded-full overflow-hidden relative">
                    <div 
                      className="absolute inset-0 bg-blue-500"
                      style={{ 
                        clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%)` 
                      }}
                    ></div>
                    <div 
                      className="absolute inset-0 bg-pink-500"
                      style={{ 
                        clipPath: `polygon(50% 50%, 100% 0, 100% 100%, 0 100%, 0 0)`,
                        transform: `rotate(${(analyticsData.genderDistribution.male / (analyticsData.genderDistribution.male + analyticsData.genderDistribution.female)) * 360}deg)` 
                      }}
                    ></div>
                  </div>
                </div>
                
                <div className="flex justify-around">
                  <div className="text-center">
                    <div className="flex items-center justify-center">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                      <span className="text-sm">Male</span>
                    </div>
                    <div className="font-medium">{analyticsData.genderDistribution.male}</div>
                    <div className="text-xs text-gray-500">
                      {Math.round((analyticsData.genderDistribution.male / (analyticsData.genderDistribution.male + analyticsData.genderDistribution.female)) * 100)}%
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center">
                      <div className="w-3 h-3 bg-pink-500 rounded-full mr-2"></div>
                      <span className="text-sm">Female</span>
                    </div>
                    <div className="font-medium">{analyticsData.genderDistribution.female}</div>
                    <div className="text-xs text-gray-500">
                      {Math.round((analyticsData.genderDistribution.female / (analyticsData.genderDistribution.male + analyticsData.genderDistribution.female)) * 100)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Subscription Upsell for Basic Analytics */}
          {!hasTier(['elevate']) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <h3 className="font-medium text-blue-800 mb-2">Upgrade to Elevate for Advanced Analytics</h3>
              <p className="text-blue-700 mb-4">
                Get access to detailed student performance insights, custom reports, and data export features.
              </p>
              <a href="/subscriptions" className="btn-primary inline-block">
                Upgrade Now
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SchoolAnalytics;
